/* eslint-disable react/prop-types */
import React, { Ref, useEffect, useMemo } from 'react';
import { Card } from 'antd';
import { MapContainer, Polyline, TileLayer, useMap } from 'react-leaflet';
import 'react-leaflet-fullscreen/dist/styles.css';
import { useJourneyDashboardContext } from '../../context';
import WaypointMarker from './WaypointMarker';
import decodePolyline from './utils';
import { LatLngBounds } from 'leaflet';
import useUserCityPosition from 'hooks/useUserCity';
import { Waypoint } from 'interfaces/journey';

export interface Point {
  lat: number;
  lng: number;
}

const Map = React.forwardRef<HTMLDivElement>((props, ref) => {
  const { center, waypoints, geometry, originAddress, destinationAddress } =
    useJourneyDashboardContext();
  const { lat, lng } = useUserCityPosition();

  const polyline: Point[] = useMemo(() => {
    if (geometry) return decodePolyline(geometry);
    return [];
  }, [geometry]);

  const RecenterAutomatically = ({
    lat,
    lng,
    zoom,
    polyline,
    originAddress,
  }: {
    lat?: number;
    lng?: number;
    zoom: number;
    polyline: Point[];
    originAddress?: Waypoint;
  }) => {
    const map = useMap();

    useEffect(() => {
      if (
        (!polyline || polyline.length === 0) &&
        originAddress &&
        originAddress.latitude &&
        originAddress.longitude
      ) {
        map.setView([originAddress.latitude, originAddress.longitude], zoom);
      }
    }, [originAddress, polyline, map]);

    useEffect(() => {
      if (lat && lng) {
        map.setView([lat, lng], zoom);
      } else if (map && geometry && polyline.length > 1) {
        const bounds = polyline.reduce(
          (acc, { lat, lng }) => acc.extend([lat, lng]),
          new LatLngBounds([{ lat: polyline[0].lat, lng: polyline[0].lng }]),
        );
        map.fitBounds(bounds, { padding: [40, 40] });
      }
    }, [polyline, lat, lng, zoom, map]);

    return null;
  };

  return (
    <Card
      style={{ overflow: 'hidden', margin: '0' }}
      styles={{ body: { padding: 0 } }}
      ref={ref as Ref<HTMLDivElement>}
    >
      <MapContainer
        style={{ height: '56vh', width: '100%' }}
        center={[
          originAddress?.latitude ?? lat,
          originAddress?.longitude ?? lng,
        ]}
        zoom={13}
      >
        <RecenterAutomatically
          polyline={polyline}
          lat={center?.lat}
          lng={center?.lng}
          originAddress={originAddress}
          zoom={waypoints && waypoints.length > 0 ? 17 : 13}
        />
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          minZoom={6}
          maxZoom={20}
        />
        {geometry ? (
          <Polyline positions={polyline} color="#254370" weight={5} />
        ) : (
          waypoints &&
          waypoints.length > 1 && (
            <Polyline
              positions={waypoints
                .filter((waypoint) => waypoint.latitude && waypoint.longitude)
                .map((wp) => [wp.latitude!, wp.longitude!])}
              color="#0DB8B2"
              weight={3}
            />
          )
        )}
        {waypoints &&
          waypoints
            .filter((waypoint) => waypoint.latitude && waypoint.longitude)
            .map((waypoint) => <WaypointMarker waypoint={waypoint} />)}
        {originAddress && originAddress.latitude && originAddress.longitude && (
          <WaypointMarker waypoint={originAddress} />
        )}
        {destinationAddress &&
          originAddress &&
          destinationAddress.address !== originAddress.address &&
          destinationAddress.latitude &&
          destinationAddress.longitude && (
            <WaypointMarker waypoint={destinationAddress} />
          )}
      </MapContainer>
    </Card>
  );
});

export default Map;
